import { Uuid, UuidString } from '~src/utils/UUID';
import { calculateSHA1 } from '~/src/utils/calculateSHA1';
import { buildQueryParams } from '~/src/utils/buildQueryParams';
import { UploadMediaResponse } from '~/src/types/mediaUpload';

export class UploadsClient {
  async upload_media(
    options: UploadMediaOptions,
    onProgress: (progress_percent: number | undefined, local_uuid: Uuid) => void,
    abort_signal: AbortSignal,
  ): Promise<UuidString> {
    const hash_progress = (progress_percent: number) => {
      onProgress(progress_percent / 2, options.local_uuid);
    };
    const hashHex = await calculateSHA1(options.file, hash_progress);

    const query_params = buildQueryParams({
      file_name: options.file.name,
      media_type: options.file.type,
      size_bytes: options.file.size,
      sha1sum: hashHex,
    });

    const xhr = new XMLHttpRequest();

    abort_signal.addEventListener('abort', () => {
      xhr.abort();
    });

    xhr.open('POST', `/api/upload/post-media?${query_params}`, true);

    xhr.upload.onprogress = function (this: XMLHttpRequest, ev: ProgressEvent) {
      if (ev.lengthComputable) {
        const percentComplete = (ev.loaded / ev.total) * 100;
        onProgress(percentComplete / 2 + 50, options.local_uuid);
      }
    };

    const response = await new Promise<UploadMediaResponse>((resolve, reject) => {
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(UploadMediaResponse.parse(JSON.parse(xhr.response)));
        } else {
          reject(new Error('File upload failed with status: ' + xhr.statusText));
        }
      };

      xhr.send(options.file);
    });

    return response.id;
  }

  async upload_profile_image(
    options: UploadProfileImageOptions,
    onProgress: (progress_percent: number | undefined) => void,
    abort_signal: AbortSignal,
  ): Promise<UuidString> {
    const hash_progress = (progress_percent: number) => {
      onProgress(progress_percent / 2);
    };
    const hashHex = await calculateSHA1(options.file, hash_progress);

    const query_params = buildQueryParams({
      group_id: options.group_id.toString(),
      file_name: options.file.name,
      media_type: options.file.type,
      size_bytes: options.file.size,
      sha1sum: hashHex,
    });

    const xhr = new XMLHttpRequest();

    abort_signal.addEventListener('abort', () => {
      xhr.abort();
    });

    xhr.open('POST', `/api/upload/profile-image?${query_params}`, true);

    xhr.upload.onprogress = function (this: XMLHttpRequest, ev: ProgressEvent) {
      if (ev.lengthComputable) {
        const percentComplete = (ev.loaded / ev.total) * 100;
        onProgress(percentComplete / 2 + 50);
      }
    };

    const response = await new Promise<UploadMediaResponse>((resolve, reject) => {
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(UploadMediaResponse.parse(JSON.parse(xhr.response)));
        } else {
          reject(new Error('File upload failed with status: ' + xhr.statusText));
        }
      };

      xhr.send(options.file);
    });

    return response.id;
  }

  async upload_group_image(
    options: UploadGroupImageOptions,
    onProgress: (progress_percent: number | undefined) => void,
    abort_signal: AbortSignal,
  ): Promise<UuidString> {
    const hash_progress = (progress_percent: number) => {
      onProgress(progress_percent / 2);
    };
    const hashHex = await calculateSHA1(options.file, hash_progress);

    const query_params = buildQueryParams({
      group_id: options.group_id.toString(),
      file_name: options.file.name,
      media_type: options.file.type,
      size_bytes: options.file.size,
      sha1sum: hashHex,
    });

    const xhr = new XMLHttpRequest();

    abort_signal.addEventListener('abort', () => {
      xhr.abort();
    });

    xhr.open('POST', `/api/upload/group-image?${query_params}`, true);

    xhr.upload.onprogress = function (this: XMLHttpRequest, ev: ProgressEvent) {
      if (ev.lengthComputable) {
        const percentComplete = (ev.loaded / ev.total) * 100;
        onProgress(percentComplete / 2 + 50);
      }
    };

    const response = await new Promise<UploadMediaResponse>((resolve, reject) => {
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(UploadMediaResponse.parse(JSON.parse(xhr.response)));
        } else {
          reject(new Error('File upload failed with status: ' + xhr.statusText));
        }
      };

      xhr.send(options.file);
    });

    return response.id;
  }
}

interface UploadMediaOptions {
  file: File;
  local_uuid: Uuid;
}

interface UploadProfileImageOptions {
  file: File;
  group_id: UuidString;
}

interface UploadGroupImageOptions {
  file: File;
  group_id: UuidString;
}
